import {
  AsyncPipe,
  DatePipe,
  I18nPluralPipe,
  NgForOf,
  NgIf,
  NgOptimizedImage,
} from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChip, MatChipListbox } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { isDefined } from '@sama/js-core';
import { map } from 'lodash';
import { map as rxjsMap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ScrollEnablerDirective } from '../../../../cross-team-shared/scroll-enabler.directive';
import { LoadImageService } from '../../../../shared/misc-services/load-image.service';
import { icons } from '../../../outputs/annotation/annotation-shared/toolbars/shape-list-and-tag-editor/feedback-list/client-issue-list.component';
import { DecisionMessageModalComponent } from '../../decision-message-modal/decision-message-modal.component';
import { Screenshot } from '../../resources/client-issue-resource.service';
import { ClientFeedbackDialogService } from '../client-feedback-dialog.service';
import { ClientFeedbackCrudService } from '../crud/client-feedback-crud.service';
import { FeedbackListService } from '../feedback-list.service';
import { FeedbackPaneComponent } from '../feedback-pane/feedback-pane.component';

@Component({
  selector: 'h3-client-issue-detail-view',
  templateUrl: './client-issue-detail-view.component.html',
  styleUrls: ['./client-issue-detail-view.component.scss', '../feedback.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIcon,
    DatePipe,
    I18nPluralPipe,
    AsyncPipe,
    FeedbackPaneComponent,
    NgIf,
    MatChipListbox,
    MatChip,
    NgForOf,
    NgOptimizedImage,
    ReactiveFormsModule,
    ScrollEnablerDirective,
    MatButton,
    MatTooltip,
    MatIconButton,
  ],
})
export class ClientFeedbackDetailViewComponent {
  issue$ = this.getIssueDetails$();
  comments$ = this.issue$.pipe(
    filter(isDefined),
    rxjsMap((issue) =>
      issue.comments.sort(
        (a, b) =>
          new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf(),
      ),
    ),
  );
  icons = icons;
  tags$ = this.issue$.pipe(rxjsMap((issue) => issue?.tags ?? []));
  form = this.formBuilder.nonNullable.group({
    comment: ['', Validators.required],
    screenshots: [[] as Screenshot[]],
  });
  commentCountMapping = {
    '=0': 'No Comments',
    '=1': '1 Comment',
    other: '# Comments',
  };

  constructor(
    private clientFeedbackDialogService: ClientFeedbackDialogService,
    private clientFeedbackCrudService: ClientFeedbackCrudService,
    private feedbackListService: FeedbackListService,
    private formBuilder: FormBuilder,
    private matDialog: MatDialog,
    private loadImageService: LoadImageService,
  ) {
    this.form.markAsTouched();
  }

  closeAndGoBackToFeedbackList() {
    this.clientFeedbackDialogService.closeView();
    this.feedbackListService.openFeedbackList('reopen');
  }

  saveComment(issueId: string) {
    const formValues = this.form.getRawValue();
    this.clientFeedbackCrudService.saveComment(
      {
        text: formValues.comment,
      },
      issueId,
      formValues.screenshots,
    );
    this.form.reset();
  }

  async addScreenshots(event: any) {
    const files = event.target.files;
    if (!files) {
      return;
    }
    const screenshots = await Promise.all(
      map(files, async (file: File) => {
        const arrayBuffer = await this.loadImageService.loadImageBlob(file);
        const blob = new Blob([arrayBuffer], { type: file.type });
        const url = URL.createObjectURL(blob);
        return { name: file.name, url, blob };
      }),
    );
    this.form.controls.screenshots.setValue([
      ...this.form.controls.screenshots.value,
      ...screenshots,
    ]);
  }

  markAsSolution(commentId: string, issueId: string) {
    const confirmationText =
      this.clientFeedbackDialogService.isCurrentUserTheIssueCreator(issueId)
        ? 'Are you sure you want to mark this issue as resolved?'
        : 'Are you sure you want to mark this issue as resolved? Please note that you are not the original creator of this issue.';
    this.matDialog
      .open(DecisionMessageModalComponent, {
        data: {
          title: 'Confirm Issue Solution',
          text: confirmationText,
        },
      })
      .afterClosed()
      .pipe(filter((resolve) => resolve))
      .subscribe(() => {
        this.clientFeedbackCrudService.markCommentAsSolution(
          commentId,
          issueId,
        );
      });
  }

  private getIssueDetails$() {
    return this.clientFeedbackDialogService.getSelectedFeedback$();
  }
}
